import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../services/api";
import { extractObject } from "../utils/apiData";
import {
  formatCurrency,
  formatDateForApi,
  formatInteger,
  toNumber,
} from "../utils/formatters";

const formatPercentage = (value, emptyLabel = "New") => {
  if (value === null || value === undefined) return emptyLabel;
  const numericValue = toNumber(value);
  return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(1)}%`;
};

const changeClassName = (value) => {
  if (value === null || value === undefined) return "text-primary";
  const numericValue = toNumber(value);
  if (numericValue > 0) return "text-success";
  if (numericValue < 0) return "text-danger";
  return "text-muted";
};

const MetricCard = ({ label, value, subtext }) => (
  <Card className="kpi-card h-100">
    <Card.Body>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-sub">{subtext}</div>
    </Card.Body>
  </Card>
);

const HeadToHeadMetric = ({ label, leftValue, rightValue, formatter }) => {
  const format = formatter || ((value) => formatInteger(value));
  const left = toNumber(leftValue);
  const right = toNumber(rightValue);

  return (
    <tr>
      <th>{label}</th>
      <td className={left > right ? "fw-bold text-success" : ""}>
        {format(left)}
      </td>
      <td className={right > left ? "fw-bold text-success" : ""}>
        {format(right)}
      </td>
    </tr>
  );
};

const normalizeShop = (shop) => ({
  ...shop,
  rank: toNumber(shop.rank),
  revenue_share_percentage: toNumber(shop.revenue_share_percentage),
  discount_rate_percentage: toNumber(shop.discount_rate_percentage),
  current: {
    revenue: toNumber(shop.current?.revenue),
    transactions: toNumber(shop.current?.transactions),
    items_sold: toNumber(shop.current?.items_sold),
    discounts: toNumber(shop.current?.discounts),
    average_order_value: toNumber(shop.current?.average_order_value),
    active_sellers: toNumber(shop.current?.active_sellers),
  },
  previous: {
    revenue: toNumber(shop.previous?.revenue),
    transactions: toNumber(shop.previous?.transactions),
    items_sold: toNumber(shop.previous?.items_sold),
    discounts: toNumber(shop.previous?.discounts),
    average_order_value: toNumber(shop.previous?.average_order_value),
    active_sellers: toNumber(shop.previous?.active_sellers),
  },
  change: {
    revenue_percentage:
      shop.change?.revenue_percentage === null
        ? null
        : toNumber(shop.change?.revenue_percentage),
    transactions_percentage:
      shop.change?.transactions_percentage === null
        ? null
        : toNumber(shop.change?.transactions_percentage),
    items_percentage:
      shop.change?.items_percentage === null
        ? null
        : toNumber(shop.change?.items_percentage),
    average_order_value_percentage:
      shop.change?.average_order_value_percentage === null
        ? null
        : toNumber(shop.change?.average_order_value_percentage),
  },
});

const ShopComparison = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comparison, setComparison] = useState(null);
  const [leftShopId, setLeftShopId] = useState("");
  const [rightShopId, setRightShopId] = useState("");

  const fetchComparison = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.getShopComparison({
        start_date: formatDateForApi(filters.startDate),
        end_date: formatDateForApi(filters.endDate),
      });

      const payload = extractObject(response);
      const shops = Array.isArray(payload.shops)
        ? payload.shops.map(normalizeShop)
        : [];

      const normalized = {
        ...payload,
        shops,
        summary: {
          ...payload.summary,
          active_shops: toNumber(payload.summary?.active_shops),
          total_revenue: toNumber(payload.summary?.total_revenue),
          previous_total_revenue: toNumber(
            payload.summary?.previous_total_revenue,
          ),
          revenue_change_percentage:
            payload.summary?.revenue_change_percentage === null
              ? null
              : toNumber(payload.summary?.revenue_change_percentage),
          total_transactions: toNumber(payload.summary?.total_transactions),
          total_items_sold: toNumber(payload.summary?.total_items_sold),
        },
      };

      setComparison(normalized);

      setLeftShopId((current) => {
        if (shops.some((shop) => String(shop.shop_id) === String(current))) {
          return current;
        }
        return shops[0]?.shop_id || "";
      });

      setRightShopId((current) => {
        if (shops.some((shop) => String(shop.shop_id) === String(current))) {
          return current;
        }
        return shops[1]?.shop_id || shops[0]?.shop_id || "";
      });
    } catch (requestError) {
      setError(requestError.message || "Shop comparison could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [filters.endDate, filters.startDate]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  const shops = comparison?.shops || [];
  const summary = comparison?.summary || {};

  const revenueChartData = useMemo(
    () =>
      shops.map((shop) => ({
        shop_name: shop.shop_name,
        current_revenue: shop.current.revenue,
        previous_revenue: shop.previous.revenue,
      })),
    [shops],
  );

  const growthChartData = useMemo(
    () =>
      shops.map((shop) => ({
        shop_name: shop.shop_name,
        growth:
          shop.change.revenue_percentage === null
            ? 0
            : shop.change.revenue_percentage,
      })),
    [shops],
  );

  const leftShop = shops.find(
    (shop) => String(shop.shop_id) === String(leftShopId),
  );
  const rightShop = shops.find(
    (shop) => String(shop.shop_id) === String(rightShopId),
  );

  if (loading && !comparison) {
    return (
      <div className="content-loader">
        <Spinner animation="border" variant="danger" role="status" />
        <span>Comparing shops…</span>
      </div>
    );
  }

  return (
    <div>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Branch performance</p>
          <h1>Shop Comparison</h1>
          <p className="text-muted mb-0 mt-2">
            The selected date range is compared with the immediately preceding
            period of the same length. The global shop filter is intentionally
            ignored so every branch can be compared.
          </p>
        </div>
        <Button variant="outline-dark" onClick={fetchComparison} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh Data"}
        </Button>
      </div>

      {error && (
        <Alert variant="danger">
          <Alert.Heading>Shop comparison could not load</Alert.Heading>
          <p className="mb-3">{error}</p>
          <Button variant="outline-danger" onClick={fetchComparison}>
            Retry
          </Button>
        </Alert>
      )}

      {comparison && (
        <>
          <Alert variant="light" className="border mb-4">
            <strong>Current:</strong> {comparison.current_period?.label}
            <span className="mx-2">•</span>
            <strong>Previous:</strong> {comparison.previous_period?.label}
          </Alert>

          <Row className="g-3 mb-4">
            <Col md={6} xl={3}>
              <MetricCard
                label="Combined Revenue"
                value={formatCurrency(summary.total_revenue)}
                subtext={`${formatPercentage(
                  summary.revenue_change_percentage,
                )} vs previous period`}
              />
            </Col>
            <Col md={6} xl={3}>
              <MetricCard
                label="Best Shop"
                value={summary.best_shop?.shop_name || "No data"}
                subtext={formatCurrency(summary.best_shop?.revenue)}
              />
            </Col>
            <Col md={6} xl={3}>
              <MetricCard
                label="Fastest Growing"
                value={summary.fastest_growing_shop?.shop_name || "No data"}
                subtext={formatPercentage(
                  summary.fastest_growing_shop?.revenue_change_percentage,
                )}
              />
            </Col>
            <Col md={6} xl={3}>
              <MetricCard
                label="Active Shops"
                value={formatInteger(summary.active_shops)}
                subtext={`${formatInteger(summary.total_transactions)} transactions`}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Card className="h-100">
                <Card.Header>Revenue by Shop</Card.Header>
                <Card.Body>
                  {revenueChartData.length ? (
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={revenueChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="shop_name"
                          interval={0}
                          angle={-20}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar
                          dataKey="previous_revenue"
                          name={comparison.previous_period?.label || "Previous"}
                          fill="#6c757d"
                        />
                        <Bar
                          dataKey="current_revenue"
                          name={comparison.current_period?.label || "Current"}
                          fill="#c8102e"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="empty-chart">No shop revenue data.</div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={5}>
              <Card className="h-100">
                <Card.Header>Revenue Growth by Shop</Card.Header>
                <Card.Body>
                  {growthChartData.length ? (
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={growthChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" unit="%" />
                        <YAxis
                          type="category"
                          dataKey="shop_name"
                          width={110}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip formatter={(value) => `${toNumber(value).toFixed(1)}%`} />
                        <Bar dataKey="growth" name="Revenue Change" fill="#202020" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="empty-chart">No growth data.</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {shops.length >= 2 && (
            <Card className="mb-4">
              <Card.Header>Head-to-Head Comparison</Card.Header>
              <Card.Body>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Label>First shop</Form.Label>
                    <Form.Select
                      value={leftShopId}
                      onChange={(event) => setLeftShopId(event.target.value)}
                    >
                      {shops.map((shop) => (
                        <option key={shop.shop_id} value={shop.shop_id}>
                          {shop.shop_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Second shop</Form.Label>
                    <Form.Select
                      value={rightShopId}
                      onChange={(event) => setRightShopId(event.target.value)}
                    >
                      {shops.map((shop) => (
                        <option key={shop.shop_id} value={shop.shop_id}>
                          {shop.shop_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                {leftShop && rightShop && (
                  <div className="table-responsive">
                    <Table bordered hover>
                      <thead>
                        <tr>
                          <th>Metric</th>
                          <th>{leftShop.shop_name}</th>
                          <th>{rightShop.shop_name}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <HeadToHeadMetric
                          label="Revenue"
                          leftValue={leftShop.current.revenue}
                          rightValue={rightShop.current.revenue}
                          formatter={formatCurrency}
                        />
                        <HeadToHeadMetric
                          label="Transactions"
                          leftValue={leftShop.current.transactions}
                          rightValue={rightShop.current.transactions}
                        />
                        <HeadToHeadMetric
                          label="Items sold"
                          leftValue={leftShop.current.items_sold}
                          rightValue={rightShop.current.items_sold}
                        />
                        <HeadToHeadMetric
                          label="Average order value"
                          leftValue={leftShop.current.average_order_value}
                          rightValue={rightShop.current.average_order_value}
                          formatter={formatCurrency}
                        />
                        <HeadToHeadMetric
                          label="Discounts"
                          leftValue={leftShop.current.discounts}
                          rightValue={rightShop.current.discounts}
                          formatter={formatCurrency}
                        />
                        <HeadToHeadMetric
                          label="Active sellers"
                          leftValue={leftShop.current.active_sellers}
                          rightValue={rightShop.current.active_sellers}
                        />
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header>Complete Shop Ranking</Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Shop</th>
                      <th>Revenue</th>
                      <th>Revenue share</th>
                      <th>Revenue change</th>
                      <th>Transactions</th>
                      <th>Average order</th>
                      <th>Items sold</th>
                      <th>Discounts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shops.map((shop) => (
                      <tr key={shop.shop_id}>
                        <td>#{formatInteger(shop.rank)}</td>
                        <td>
                          <strong>{shop.shop_name}</strong>
                          {shop.shop_address && (
                            <small className="d-block text-muted">
                              {shop.shop_address}
                            </small>
                          )}
                        </td>
                        <td>{formatCurrency(shop.current.revenue)}</td>
                        <td>{shop.revenue_share_percentage.toFixed(1)}%</td>
                        <td className={`fw-semibold ${changeClassName(shop.change.revenue_percentage)}`}>
                          {formatPercentage(shop.change.revenue_percentage)}
                        </td>
                        <td>{formatInteger(shop.current.transactions)}</td>
                        <td>{formatCurrency(shop.current.average_order_value)}</td>
                        <td>{formatInteger(shop.current.items_sold)}</td>
                        <td>{formatCurrency(shop.current.discounts)}</td>
                      </tr>
                    ))}
                    {!shops.length && (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          No shop activity was found for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default ShopComparison;
